import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import {
	CATEGORY_ROWS_PAGE_SIZE,
	closeImportCategoryRows,
	loadImportCategoryRows,
} from '../../../redux/actions/import.js';
import Button from '../../components/Button.jsx';
import Modal from '../../components/Modal.jsx';
import ImportRowCategory from './ImportRowCategory.jsx';
import ImportRowNotes from './ImportRowNotes.jsx';
import { formatRowDate, statusLabel, STATUS_EXPLANATIONS } from './importRowDisplay.js';

/**
 * The transactions behind one line of the category mapper.
 *
 * The mapper decides in bulk -- one rule moves every row under a category at once -- and a name and
 * a count are often not enough to decide with: an export's "Shopping" is groceries and a laptop and
 * a refund, and which of those it mostly is only shows in the rows. This is the way to look before
 * choosing, without leaving the screen the choice is made on.
 *
 * Rows arrive resolved by the same service the preview uses, so each one also says what the current
 * rules would do with it -- including the ones no rule of this category can fix, like a row blocked
 * on its account or already imported.
 */
export default function ImportCategoryRowsModal() {
	const dispatch = useDispatch();
	const batch = useSelector((state) => state.importing.selectedBatch);
	const view = useSelector((state) => state.importing.categoryRows);
	const loading = useSelector((state) => state.importing.loadingCategoryRows);

	if(!batch || !view) {
		return null;
	}

	const close = () => dispatch(closeImportCategoryRows());
	const goTo = (offset) => dispatch(loadImportCategoryRows(
		batch.id, view.externalCategory, view.externalMerchant, offset
	));

	return (
		<Modal
			className='import-category-rows-modal'
			header={heading(view)}
			onClose={close}
			buttons={[{ text: 'Close', onClick: close }]}
		>
			{/**
			  * The page in hand stays on screen while the next one is fetched, dimmed rather than
			  * replaced by a loader: a popup that empties itself between pages is unreadable to page
			  * through, and the rows being replaced are the ones a person is in the middle of reading.
			  */}
			<div className={'import-category-rows' + (loading ? ' import-category-rows-loading' : '')}>
				<p className='grey-text'>
					{view.total} {view.total === 1 ? 'transaction' : 'transactions'} totalling{' '}
					{moneyFormatFromCents(view.amountTotal)} in this file, as the export wrote them. The
					result column is what the mappings currently say would happen to each one.
				</p>

				<table className='import-table'>
					<thead>
						<tr>
							<th>Date</th>
							<th>Merchant</th>
							<th>In the file</th>
							<th className='right-align'>Amount</th>
							<th>Result</th>
						</tr>
					</thead>
					<tbody>
						{view.rows.map((row) => (
							<tr key={row.id} className={`import-row-${row.status}`}>
								<td className='grey-text'>{formatRowDate(row.date)}</td>
								<td>
									{row.merchant || <span className='grey-text'>(no merchant)</span>}
									{/**
									  * What the bank actually put on the statement. It is the half of a row that
									  * says what an unrecognisable merchant name was, which is exactly what is
									  * being looked for here.
									  */}
									{!!row.original_statement && row.original_statement !== row.merchant &&
									<div className='import-external-key'>{row.original_statement}</div>
									}
									<ImportRowNotes notes={row.notes} />
								</td>
								<td className='grey-text'>
									{row.external_category}
									<div className='import-external-key'>{row.external_account}</div>
								</td>
								<td className='right-align'>{moneyFormatFromCents(row.amount)}</td>
								<td>
									<span
										className={`import-status import-status-${row.status}`}
										title={STATUS_EXPLANATIONS[row.status] || ''}
									>
										{statusLabel(row.status)}
									</span>
									{!!row.category_name &&
									<div className='import-external-key'>{row.category_name}</div>
									}
									{!!row.blocked_on &&
									<div className='import-external-key'>{row.blocked_on}</div>
									}
									<ImportRowCategory row={row} />
								</td>
							</tr>
						))}
					</tbody>
				</table>

				{!view.rows.length && !loading &&
				<p className='grey-text'>Nothing came back for this one.</p>
				}

				{view.total > CATEGORY_ROWS_PAGE_SIZE &&
				<div className='import-pager'>
					<Button
						small
						flat
						disabled={view.offset <= 0 || loading}
						onClick={() => goTo(Math.max(0, view.offset - CATEGORY_ROWS_PAGE_SIZE))}
					>
						Previous
					</Button>
					<span className='grey-text'>
						{view.offset + 1}&ndash;{Math.min(view.offset + CATEGORY_ROWS_PAGE_SIZE, view.total)}
						{' '}of {view.total}
					</span>
					<Button
						small
						flat
						disabled={view.offset + CATEGORY_ROWS_PAGE_SIZE >= view.total || loading}
						onClick={() => goTo(view.offset + CATEGORY_ROWS_PAGE_SIZE)}
					>
						Next
					</Button>
				</div>
				}
			</div>
		</Modal>
	);
}

/**
 * Which line was opened. A merchant is shown under its category rather than alone, because the same
 * merchant can appear in several of them and the rows here are only the ones from this one.
 */
function heading(view) {
	const category = view.externalCategory || '(no category)';
	if(view.externalMerchant === null) {
		return category;
	}
	return `${category} — ${view.externalMerchant || '(no merchant)'}`;
}
